import { serialize } from "object-to-formdata";
import request from "../../utils/request";
import {
  CompanyView, Company, CompanyInput, Result, SubscriptionPlan, WorkplaceModel, ExtendTrialInput, ActivateTrialCompanyInput,
  Branch, Department, CompanyRulesView, CompanyRulesInput,
  BranchInput,
  DepartmentInput,
  BatchMigrationSetupResult
} from "../../models/general";
import { WeekScheduleView, WeekScheduleInput,UserActivityView } from "../../models/attendance";


// export async function getLeavePerUser(userId: string) {
//    const response = await request.get<Result<LeavesModel[]>>(`Leave/GetLeavePerUser/${userId}`);
//    return response.data;
// }

// export async function getBalancePerUser(userId: string) {
//    const response = await request
//        .get<Result<LeaveBalanceModel[]>>(`Leave/GetBalancePerUser/${userId}`);
//    return response.data;
// }

export async function getCompanies(organizationId?: number) {
  const response = await request
    .get<Result<CompanyView[]>>(`Company/GetCompanies/${organizationId ?? ""}`);
  return response.data;
}

export async function getCompany(id: string | number) {
  const response = await request.get<Result<CompanyView>>(`Company/${id}`);
  return response.data;
}

export async function addCompany(input: CompanyInput) {
  const response = await request
    .post<Result<Company>>("Company/AddCompany", serialize(input));
  return response.data;
}

export async function extendTrial(input: ExtendTrialInput) {
  const response = await request
    .post<Result<boolean>>("Company/ExtendTrial", input);
  return response.data;
}

export async function activateTrialCompany(input: ActivateTrialCompanyInput) {
  const response = await request
    .post<Result<boolean>>("Company/ActivateTrialCompany", input);
  return response.data;
}

export async function getPlans() {
  const response = await request.get<Result<SubscriptionPlan[]>>("Company/Plans");
  return response.data;
}

export async function updateCompany(id: number, input: CompanyInput) {
  // Build FormData manually with PascalCase keys to match the [DataContract] CompanyInput.
  const fd = new FormData();
  const append = (key: string, val: unknown) => {
    if (val === undefined || val === null) return;
    if (val instanceof File) { fd.append(key, val); return; }
    fd.append(key, String(val));
  };

  append('Name', input.name);
  append('CountryId', input.countryId);
  append('TimeZone', input.timeZone);
  append('PlanId', input.planId);
  append('OrganizationId', input.organizationId);
  append('Address', input.address);
  append('PhoneNumber', input.phoneNumber);
  append('Email', input.email);
  append('WebsiteLink', input.websiteLink);
  append('PrimaryColor', input.primaryColor);
  append('Locales', input.locales);
  append('AdminFirstName', input.adminFirstName);
  append('AdminLastName', input.adminLastName);
  append('AdminEmail', input.adminEmail);
  append('EmployerNarrative', input.employerNarrative);
  append('MOLNumber', input.molNumber);
  append('CommercialRegistrationNumber', input.commercialRegistrationNumber);
  append('MudadEstablishmentNumber', input.mudadEstablishmentNumber);
  append('QiwaEntityId', input.qiwaEntityId);
  append('BankRoutingCode', input.bankRoutingCode);
  append('WpsEmployeeThreshold', input.wpsEmployeeThreshold);
  if (input.image) fd.append('Image', input.image as unknown as File);
  if (input.rolesMaxUsers) {
    for (const [role, max] of Object.entries(input.rolesMaxUsers)) {
      fd.append(`RolesMaxUsers[${role}]`, String(max));
    }
  }

  const response = await request.put<Result<Company>>(`Company/${id}`, fd);
  return response.data;
}

export async function deleteCompany(id: number) {
  const response = await request.delete<Result<Record<string, never>>>(`Company/${id}`);
  return response.data;
}

export async function getUsersLocation(companyId: number, _onlyAvailable: boolean) {
  const response = await request.get<Result<UserActivityView[]>>(`Company/${companyId}/Location`);
  return response.data;
}

export async function getWorkplaces(companyId: number, userId: string, userSpecificOnly?: boolean) {
  const params = new URLSearchParams();
  if (userId) params.append("userId", userId);
  if (userSpecificOnly) params.append("userSpecificOnly", "true");
  const qs = params.toString();
  const response = await request.get<Result<WorkplaceModel[]>>(
    `Company/${companyId}/Workplaces${qs ? `?${qs}` : ""}`,
  );
  return response.data;
}

export async function addWorkplace(companyId: number, userId: string, workplace: WorkplaceModel) {
  const response = await request.post<Result<WorkplaceModel>>(`Company/AddWorkplace/${companyId}/${userId}`, workplace);
  return response.data;
}

export async function removeWorkplace(workplaceId: number) {
  const response = await request.delete<Result<boolean>>(`Company/Workplaces/${workplaceId}`);
  return response.data;
}

export async function updateWorkplace(workplaceId: number, workplace: WorkplaceModel) {
  const response = await request.put<Result<WorkplaceModel>>(`Company/EditWorkplace/${workplaceId}`, workplace);
  return response.data;
}

export async function getBranches(companyId: number) {
  const response = await request.get<Result<Branch[]>>(`Company/Branches/${companyId}`);
  return response.data;
}

export async function getDepartments(companyId: number) {
  const response = await request.get<Result<Department[]>>(`Company/Departments/${companyId}`);
  return response.data;
}
export async function getCountryDepartments(countryId: number[], companyId: number) {
  const response = await request.post<Result<Department[]>>(`Company/GetCountryDepartments/${companyId}`, countryId);
  return response.data;
}
export async function getCountryBranches(countryId: number[], companyId: number) {
  const response = await request.post<Result<Branch[]>>(`Company/GetCountryBranches/${companyId}`, countryId);
  return response.data;
}
export async function getBranchDepartments(branchId: number[], companyId: number) {
  const response = await request.post<Result<Department[]>>(`Company/GetBranchDepartments/${companyId}`, branchId);
  return response.data;
}
export async function addUpdateBranches(branches: BranchInput[]) {
  const response = await request.post<Result<Branch[]>>(`Company/AddUpdateBranches`, branches);
  return response.data;
}

export async function addUpdateDepartments(departments: DepartmentInput[]) {
  const response = await request.post<Result<Department[]>>(`Company/AddUpdateDepartments`, departments);
  return response.data;
}

export async function deleteBranches(branchIds: number[]) {
  const response = await request.delete<Result<string>>(`Company/Branches`, { data: branchIds });
  return response.data;
}

export async function deleteDepartments(departmentIds: number[]) {
  const response = await request.delete<Result<string>>(`Company/Departments`, { data: departmentIds });
  return response.data;
}

export async function batchMigrationSetup(companyId: number, input: { branches: BranchInput[]; departments: DepartmentInput[] }) {
  const response = await request.post<Result<BatchMigrationSetupResult>>(`Company/BatchMigrationSetup/${companyId}`, input);
  return response.data;
}

export async function getCompanyRules(companyId: number) {
  const response = await request.get<Result<CompanyRulesView>>(`Company/GetCompanyRules/${companyId}`);
  return response.data;
}

export async function updateCompanyRules(companyId: number, input: CompanyRulesInput) {
  const response = await request.post<Result<CompanyRulesView>>(`Company/UpdateCompanyRules/${companyId}`, input);
  return response.data;
}

export async function operatorUpdateCompanyRules(companyId: number, input: CompanyRulesInput) {
  const response = await request.post<Result<CompanyRulesView>>(`Company/OperatorUpdateCompanyRules/${companyId}`, input);
  return response.data;
}

export async function getWeekSchedule(companyId: string | number) {
  const response = await request.get<Result<WeekScheduleView>>(`Company/GetWeekSchedule/${companyId}`);
  return response.data;
}
export async function setWeekSchedule(details: WeekScheduleInput) {
  const response = await request.post<Result<WeekScheduleView>>(`Company/SetWeekSchedule`, details);
  return response.data;
}
